// The chat, run on the user's own computer: their runner drives the Claude Code or Codex they already
// use, on their own plan, and what it does comes back over the board's socket. The conversation here is
// for showing it; the agent keeps its own, under the thread the runner names, kept on its replies.
import { useEffect, useState } from "react";
import type { BetaContentBlock, BetaMessageParam } from "@anthropic-ai/sdk/resources/beta/messages/messages";
import type { RunnerEvent, RunnerState, RunnerTurn } from "../../shared/types";
import { api } from "../lib/api";
import { get, useStore } from "../state/store";
import type { Provider } from "./config";
import { answerText } from "./questions";
import { saveNow } from "./chats";
import { chat, convo, freshForReply, loadConversation, parkSaved, saveConvo, sessionHost, type ConvoRef, type ToolState } from "./store";
import { keepHandsFree, takeBoardWords } from "./handsFree";
import { readStored, removeStored, writeStored, writeStoredJson } from "../lib/stored";

export type AgentId = "claude" | "codex";
export const agentOf = (p: Provider): AgentId => (p === "openai" ? "codex" : "claude");
/** What the chat is called when it runs on the user's computer: the app doing the work. */
export const runnerName = (p: Provider) => (p === "openai" ? "Codex" : "Claude Code");

const LIVE = "https://board.contextbolt.com";

/** Which line a computer takes: Windows's, or the one for a Mac or Linux. */
export type RunnerOs = "windows" | "unix";

/** The computer this page is open on, which is most often the one the user sets up. */
export function browserOs(): RunnerOs {
  if (typeof navigator === "undefined") return "unix";
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform || navigator.platform || "";
  return /^win/i.test(platform) || /Windows/.test(navigator.userAgent) ? "windows" : "unix";
}

const OS_KEY = "cb:runner-os";

/** The line the user picked last, else this computer's, kept for the next time. */
export function useRunnerOs(): [RunnerOs, (os: RunnerOs) => void] {
  const [os, setOs] = useState<RunnerOs>(() => {
    const saved = readStored(OS_KEY);
    return saved === "windows" || saved === "unix" ? saved : browserOs(); // no storage: this computer's
  });
  const choose = (next: RunnerOs) => {
    setOs(next);
    writeStored(OS_KEY, next); // kept for this window only when storage is out of reach
  };
  return [os, choose];
}

/**
 * The one line that pairs a computer and runs the chat there, with the one-time code: a script from this
 * board's own address that brings Node.js too when the computer has none (worker/install.ts). Windows's
 * runs the same in PowerShell or the Command Prompt, whatever the computer's script policy.
 */
export const runnerCommand = (code: string, os: RunnerOs = browserOs()) => {
  const origin = typeof location === "undefined" ? LIVE : location.origin;
  return os === "windows" ? `powershell -c "irm ${origin}/install.ps1?c=${code} | iex"` : `curl -fsSL ${origin}/install.sh | sh -s ${code}`;
};

/** Where the line runs, as the user knows it. */
export const runnerShell = (os: RunnerOs) => (os === "windows" ? "PowerShell or the Command Prompt" : "Terminal");

/**
 * The code last fetched, kept for every place that shows the line, so opening Your computers again shows it at
 * once rather than asking for another (the board gives five in a burst). It is good until a computer pairs,
 * which spends it: a new computer among the paired ones means a new code.
 */
let held: { code: string; expiresAt: number; paired: string } | null = null;
/** the one ask in flight, shared by every line on the page, so a computer that links asks for one code, not one a line */
let asking: { paired: string; p: Promise<{ code: string; expiresAt: number }> } | null = null;

function askCode(paired: string): Promise<{ code: string; expiresAt: number }> {
  if (asking?.paired === paired) return asking.p;
  const p = api<{ code: string; expiresAt: number }>("/api/runner/code", { body: {} })
    .then((r) => {
      held = { code: r.code, expiresAt: r.expiresAt, paired };
      return r;
    })
    .finally(() => {
      if (asking?.p === p) asking = null;
    });
  asking = { paired, p };
  return p;
}

/** The board and the computers paired with it now, by key, so a code is never shown on another board, or again once a computer spent it. */
const pairedNow = (s: ReturnType<typeof get>) =>
  `${s.workspace?.id ?? ""}:${s.keys
    .filter((k) => k.kind === "runner")
    .map((k) => k.id)
    .join(",")}`;

/** A one-time pairing code for the runner command, while it is wanted: fetched, and fetched again before it runs out or once a computer pairs. */
export function useRunnerCode(wanted: boolean): string | null {
  const paired = useStore(pairedNow);
  const fresh = () => (held && held.paired === paired && held.expiresAt - Date.now() > 90_000 ? held.code : null);
  const [code, setCode] = useState<string | null>(fresh);
  useEffect(() => {
    if (!wanted) return;
    let gone = false;
    let next: ReturnType<typeof setTimeout> | undefined;
    const later = (expiresAt: number) => (next = setTimeout(fetchCode, Math.max(60_000, expiresAt - Date.now() - 60_000)));
    const fetchCode = () =>
      askCode(paired)
        .then((r) => {
          if (gone) return;
          setCode(r.code);
          later(r.expiresAt);
        })
        .catch(() => {
          if (!gone) next = setTimeout(fetchCode, 15_000);
        });
    const kept = fresh();
    if (kept) {
      setCode(kept);
      later(held!.expiresAt);
    } else {
      // the code on show was spent: say so by showing none until the next one comes
      setCode(null);
      void fetchCode();
    }
    return () => {
      gone = true;
      if (next) clearTimeout(next);
    };
  }, [wanted, paired]); // eslint-disable-line react-hooks/exhaustive-deps
  return code;
}

/**
 * While the chat shows someone that the agent they chose is missing or signed out on their computer, the
 * runner there looks again every minute, so one installed or signed in to shows up within the minute the
 * chat promises. Otherwise the runner looks only every few hours, and the computer is left alone.
 */
export function useRunnerRecheck(wanted: boolean) {
  useEffect(() => {
    if (!wanted) return;
    const look = () => void api("/api/runner/recheck", { body: {} }).catch(() => {});
    look();
    const t = setInterval(look, 60_000);
    return () => clearInterval(t);
  }, [wanted]);
}

/** Whether the user's runner can run the chat for this provider right now. */
export function runnerReady(runner: RunnerState | null | undefined, p: Provider): boolean {
  return !!runner?.online && runner.agents.some((a) => a.id === agentOf(p) && a.ready);
}

export const runnerNow = (p: Provider) => runnerReady(get().runner, p);

/** A runner's reply, as it is kept in the conversation: who ran it, the thread to go on with, on which computer, and its turn. */
type RunnerReply = BetaMessageParam & { runner?: { agent: AgentId; thread: string | null; host?: string | null; turn?: string }; oai?: true };

/** What Claude Code and Codex say when the session asked for is not on the computer (checked 16 Oct 2026). */
const NO_SESSION = /No conversation found with session ID|no rollout found for thread id/i;

/**
 * Why a conversation could not go on in its session, in words: it was kept on another computer, or that
 * computer no longer has it. Its messages stay to read either way.
 */
export function noSessionError(name: string, kept: string | null, now: string | null): { title: string; body: string } {
  const elsewhere = !!kept && !!now && kept !== now;
  return {
    title: elsewhere ? `This chat's session is on ${kept}, not ${now}.` : `${name} on ${now ?? "your computer"} no longer has this chat's session.`,
    body: elsewhere
      ? `${name} keeps each chat's session on the computer it ran on. Make ${kept} the main computer in Connect to go on with it, or start a new chat. Its messages stay here to read.`
      : `It may have been cleared there. Its messages stay here to read. Start a new chat to carry on.`,
  };
}

/** The thread the agent keeps for this conversation, from the last reply the runner gave in it. */
export function runnerThread(messages: BetaMessageParam[], agent: AgentId): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const r = (messages[i] as RunnerReply).runner;
    if (r) return r.agent === agent ? r.thread : null;
  }
  return null;
}

/** Whether a conversation can go on here: one the runner wrote with this agent, or one nobody has replied in. */
export function runnerCanContinue(messages: BetaMessageParam[], agent: AgentId): boolean {
  const replies = messages.filter((m) => m.role === "assistant") as RunnerReply[];
  return replies.every((m) => m.runner?.agent === agent);
}

interface Listener {
  turn: string;
  agent: AgentId;
  on: (ev: RunnerEvent, seq?: number) => void;
  /** while the computer's link is down: the runner keeps the turn's events and sends them when it is back */
  grace?: ReturnType<typeof setTimeout>;
  /** a turn picked up after a reload that the computer has said nothing about yet */
  quiet?: ReturnType<typeof setTimeout>;
  /** asks the board for every event the turn has said, and builds the reply again from them: true while it still runs, null when the board could not be asked */
  catchUp: () => Promise<boolean | null>;
}
const listeners = new Map<string, Listener>();

/** Calls the agent on the computer waits on the user's yes for, oldest first: each chat shows its own, one at a time. */
interface Ask {
  turn: string;
  /** the chat the turn is in */
  chat: string;
  id: string;
  toolId: string;
  title: string;
  detail: string | null;
  /** in a hands-free run, why it still asks: it sends, deletes or buys, or its name says nothing known (Features 4) */
  why?: string;
}
let asks: Ask[] = [];

function showAsk(id: string) {
  const a = asks.find((x) => x.chat === id);
  convo(id).set({ approval: a ? { toolId: a.toolId, title: a.title, detail: a.detail, url: null, ...(a.why ? { why: a.why } : {}) } : null });
}

/** A call's mark that it asked the user first, kept as its state changes (Features 4). */
const keepAsked = (t: ToolState | undefined) => (t?.asked ? { asked: t.asked } : {});

/** The user's answer to the call shown, sent to the computer. False when no call there is waiting. */
export function answerRunnerAsk(allow: boolean): boolean {
  const here = chat.get().chatId;
  const a = asks.find((x) => x.chat === here);
  if (!a) return false;
  asks = asks.filter((x) => x !== a);
  chat.set((s) => ({ tools: { ...s.tools, [a.toolId]: { ...keepAsked(s.tools[a.toolId]), status: allow ? "running" : "declined" } } }));
  showAsk(here);
  void api("/api/chat/approve", { body: { turn: a.turn, id: a.id, allow } }).catch(() => convo(a.chat).set({ error: { title: "Your answer did not reach your computer.", body: "Check it is on, then try again.", action: null } }));
  return true;
}

/**
 * The user's answers to the question the agent on their computer asked, sent there. Their answer shows on the
 * question's line at once, and stays in the chat. False when no question there is waiting.
 */
export function answerRunnerQuestion(answers: Record<string, string[]>): boolean {
  const q = chat.get().question;
  if (!q?.turn) return false;
  const c = convo(chat.get().chatId);
  const said = `You answered: ${answerText(q.questions, answers)}`;
  c.set((s) => ({ question: null, tools: { ...s.tools, [q.toolId]: { status: "running", text: said } } }));
  // an answer that did not get there puts the question back, to answer again
  void api("/api/chat/answer", { body: { turn: q.turn, id: q.id, answers } }).catch((e: Error) =>
    c.set((s) => ({ question: q, tools: { ...s.tools, [q.toolId]: { status: "waiting" } }, error: { title: "Your answer did not reach your computer.", body: e?.message || "Check it is on, then answer again.", action: null } })),
  );
  return true;
}

/** Every chat event the board sends, for the turn that is listening. seq: its number in the turn, when the board keeps it. */
export function onRunnerEvent(turn: string, ev: RunnerEvent, seq?: number) {
  listeners.get(turn)?.on(ev, seq);
}

/**
 * The page is back (its link to the board came back, or the screen woke): each turn it follows asks the board
 * for what it said meanwhile, since a phone that locks or a tab put to sleep hears nothing while it is away.
 */
export function catchUpRunnerTurns() {
  for (const l of [...listeners.values()]) void l.catchUp();
}

/**
 * The turns this browser sent and has not seen end, kept for its board with the chat each is in, so a page that was
 * closed or put away while the computer finished them (a phone's browser drops a tab it put aside) gets each reply
 * in its own chat when it opens. One chat's reply can run while another's does (19 Oct 2026).
 */
const PENDING = (workspaceId: string) => `cb:chat-turns:${workspaceId}`;
/** where the one turn was kept before several could run */
const PENDING_BEFORE = (workspaceId: string) => `cb:chat-turn:${workspaceId}`;
interface Pending {
  turn: string;
  agent: AgentId;
  chat: string;
  at: number;
}

function pendingTurns(): Pending[] {
  const ws = get().workspace?.id;
  if (!ws) return [];
  try {
    const before = localStorage.getItem(PENDING_BEFORE(ws));
    if (before) {
      localStorage.removeItem(PENDING_BEFORE(ws));
      const one = JSON.parse(before) as Pending | null;
      if (one && typeof one.turn === "string") localStorage.setItem(PENDING(ws), JSON.stringify([one]));
    }
    const list = JSON.parse(localStorage.getItem(PENDING(ws)) ?? "[]") as Pending[];
    // the board keeps a turn's events for an hour after it ends
    return Array.isArray(list) ? list.filter((p) => p && typeof p.turn === "string" && typeof p.chat === "string" && Date.now() - p.at < 2 * 60 * 60_000) : [];
  } catch {
    return [];
  }
}

function writePending(list: Pending[]) {
  const ws = get().workspace?.id;
  if (!ws) return;
  // no storage: this page only
  if (list.length) writeStoredJson(PENDING(ws), list.slice(-20));
  else removeStored(PENDING(ws));
}
const keepPending = (p: Pending) => writePending([...pendingTurns().filter((x) => x.turn !== p.turn), p]);
const dropPending = (turn: string) => writePending(pendingTurns().filter((x) => x.turn !== turn));

/** The runner's state changed: a turn rides out a short drop, and ends if its computer stays away. */
export function onRunnerState(r: RunnerState) {
  for (const l of [...listeners.values()]) {
    const ready = r.online && r.agents.some((a) => a.id === l.agent && a.ready);
    if (ready) {
      if (l.grace) clearTimeout(l.grace);
      l.grace = undefined;
    } else if (!l.grace)
      l.grace = setTimeout(() => {
        // the computer may have finished while this page was away and only its link has dropped since: the board has the reply
        void l.catchUp().then((running) => listeners.get(l.turn) === l && running !== false && l.on({ kind: "error", message: r.online ? "That agent stopped being ready on your computer." : "Your computer went offline before it finished.", code: "offline" }));
      }, 90_000);
  }
}

/** How long the computer has to end a turn after Stop, before the board is asked to end it. */
const STOP_WAIT = 8_000;

/** Stops the reply running in the chat on screen. */
export function stopRunnerTurn() {
  const turn = chat.get().turn;
  if (!turn) return;
  void api("/api/chat/stop", { body: { turn } }).catch(() => {});
  // a computer that has not ended it by then (offline, or a runner stuck on it) is passed over: the board ends it,
  // and if the board cannot be reached either, the page does, so Stop always frees the chat (19 Oct 2026)
  setTimeout(() => {
    if (!listeners.has(turn)) return;
    void api("/api/chat/stop", { body: { turn, force: true } })
      .catch(() => null)
      .then(() => listeners.get(turn)?.catchUp())
      .then(() => listeners.get(turn)?.on({ kind: "error", message: "The board did not answer, so the chat stopped waiting for that reply.", code: "offline" }));
  }, STOP_WAIT);
}

const newTurnId = () => "t" + Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => (b % 36).toString(36)).join("");

/**
 * One turn on the user's computer, in the chat it was sent from. The reply builds in that chat's draft as it
 * arrives, on screen or not, and lands in its conversation when the turn ends, with the thread to go on with.
 * Returns whether it finished.
 */
export function runnerTurn(c: ConvoRef, o: { provider: Provider; /** null: the agent's own default */ model: string | null; effort: string | null; text: string; board: string | null; /** what the user has open, as the chat's context block */ view?: string | null; /** pictures, shrunk in the browser, passed to the computer as they came (the board keeps its own copy, sent apart: run.ts keepChatPictures) */ pictures?: { media_type: string; data: string }[]; /** the Button pressed, whose run the board keeps (Round 7) */ button?: string | null }): Promise<boolean> {
  const agent = agentOf(o.provider);
  const turn = newTurnId();
  const thread = runnerThread(c.get()?.messages ?? [], agent);
  // kept before it is sent, with the message, so a page closed before the reply comes still gets it. A Button's new chat
  // has no copy in this browser to send on later, so it reaches the board before its run begins: every device opens it,
  // and the page that pressed it, closed straight away, finds it there for the reply when it opens again
  // Hands-free (Features 4): the chat's own switch, kept with it from its first message, for a message the user sent in
  // it. A Button's press goes by the Button's own, which the board reads from it; words the board queued by itself (a
  // plan's carry on) never go hands-free
  const hands = !o.button && !takeBoardWords(o.text) && keepHandsFree(c);
  saveConvo(c.id);
  keepPending({ turn, agent, chat: c.id, at: Date.now() });
  const body = { turn, agent, model: o.model, effort: o.effort, text: o.text, thread, board: o.board, chat: c.id, ...(o.view ? { view: o.view } : {}), ...(o.pictures?.length ? { pictures: o.pictures } : {}), ...(o.button ? { button: o.button } : {}), ...(hands ? { handsFree: true } : {}) };
  return followTurn(turn, agent, thread, c, () => (o.button ? saveNow(c.id) : Promise.resolve()).then(() => api("/api/chat/turn", { body })));
}

const providerOf = (a: AgentId): Provider => (a === "codex" ? "openai" : "anthropic");

/**
 * The end of a turn in a chat, however it was followed: the chat is free, and kept. A chat that was left, with a
 * message the user wrote in it while the reply ran, sends that next (run.ts gives it the way to send).
 */
let sendWaiting: ((c: ConvoRef) => boolean) | null = null;
export function onWaitingMessage(fn: (c: ConvoRef) => boolean) {
  sendWaiting = fn;
}
export function turnOver(c: ConvoRef) {
  c.set({ status: "idle" });
  if (!c.here() && sendWaiting?.(c)) return;
  saveConvo(c.id);
}

/**
 * The turns the computer is still running when this page opens (a reload in the middle of a reply, or another
 * tab): each is followed to the end in its own chat, on screen when it is the one open and set aside when not, and
 * what comes is kept as the reply. A turn whose chat this browser does not know (sent from another device, or
 * before turns named their chat) is followed in the chat on screen, when that is its agent's and free.
 */
export function adoptRunnerTurns(turns: RunnerTurn[] = get().runnerTurns) {
  loadConversation(get().workspace?.id ?? null);
  const mine = pendingTurns();
  for (const t of turns) {
    if (listeners.has(t.turn) || adopting.has(t.turn)) continue;
    const s = chat.get();
    const id = t.chat ?? mine.find((p) => p.turn === t.turn)?.chat ?? null;
    const go = (ref: ConvoRef) => {
      // one the computer has not said a word about yet may never have reached it: a minute and a half of
      // silence ends it, where a turn under way waits as long as any
      void followTurn(t.turn, t.agent, runnerThread(ref.get()?.messages ?? [], t.agent), ref, undefined, t.heard ? undefined : 90_000).finally(() => turnOver(ref));
      // a question it was waiting on when the page opened shows again, to answer there
      if (t.ask) onRunnerEvent(t.turn, t.ask);
      if (t.question) onRunnerEvent(t.turn, t.question);
      // and what it said before the page opened
      void listeners.get(t.turn)?.catchUp();
    };
    adopting.add(t.turn);
    if (id && id !== s.chatId) {
      // its chat, the newer of this browser's copy and the board's, set aside to take the reply
      void parkSaved(id, providerOf(t.agent), t.turn)
        .then((ref) => ref && !listeners.has(t.turn) && onTheWay(ref, t.turn) && go(ref), tryAgainSoon)
        .finally(() => adopting.delete(t.turn));
    } else if ((id === s.chatId || !id) && s.status !== "running" && t.agent === agentOf(s.config.provider)) {
      // the chat on screen, made current from the board first, so the reply never lands on an older copy
      const here = s.chatId;
      void freshForReply(here)
        .then(() => {
          const now = chat.get();
          if (now.chatId !== here || now.status === "running" || listeners.has(t.turn)) return;
          chat.set({ status: "running", turn: t.turn, error: null, stopped: null });
          go(convo(here));
        })
        .finally(() => adopting.delete(t.turn));
    } else adopting.delete(t.turn);
  }
  adoptPending(turns);
}

/** A reply whose chat could not be read from the board (no link for a moment) is picked up again a little later. */
let again: ReturnType<typeof setTimeout> | null = null;
function tryAgainSoon() {
  if (again) return;
  again = setTimeout(() => {
    again = null;
    adoptRunnerTurns();
  }, 15_000);
}

/** Turns being picked up while their chat is read from the board: each is followed once. */
const adopting = new Set<string>();

/**
 * A chat that came back on screen while it was read (the user opened it meanwhile) follows its reply there; one set
 * aside already is. False when the screen is busy with another reply, which this one does not take over.
 */
function onTheWay(ref: ConvoRef, turn: string): boolean {
  if (!ref.here()) return true;
  const s = chat.get();
  if (s.status === "running" && s.turn !== turn) return false;
  chat.set({ status: "running", turn, error: null, stopped: null });
  return true;
}

/** Turns this browser sent that ended while the page was closed: each reply, from the board, lands in its own chat. */
function adoptPending(turns: RunnerTurn[]) {
  for (const p of pendingTurns()) {
    if (listeners.has(p.turn) || adopting.has(p.turn) || turns.some((x) => x.turn === p.turn)) continue;
    const s = chat.get();
    const go = (ref: ConvoRef) => {
      void followTurn(p.turn, p.agent, runnerThread(ref.get()?.messages ?? [], p.agent), ref).finally(() => turnOver(ref));
      void listeners.get(p.turn)?.catchUp();
    };
    if (p.chat === s.chatId) {
      if (s.status === "running" || p.agent !== agentOf(s.config.provider)) continue;
      const here = s.chatId;
      adopting.add(p.turn);
      void freshForReply(here)
        .then(() => {
          const now = chat.get();
          if (now.chatId !== here || now.status === "running" || listeners.has(p.turn)) return;
          chat.set({ status: "running", turn: p.turn, error: null, stopped: null });
          go(convo(here));
        })
        .finally(() => adopting.delete(p.turn));
      continue;
    }
    adopting.add(p.turn);
    void parkSaved(p.chat, providerOf(p.agent), p.turn)
      .then((ref) => {
        // a chat neither this browser nor the board has: nowhere for the reply to go
        if (!ref) return dropPending(p.turn);
        if (!listeners.has(p.turn) && onTheWay(ref, p.turn)) go(ref);
      }, tryAgainSoon)
      .finally(() => adopting.delete(p.turn));
  }
}

function followTurn(turn: string, agent: AgentId, from: string | null, c: ConvoRef, send?: () => Promise<unknown>, firstWord?: number): Promise<boolean> {
  c.set({ turn });
  let thread = from;
  const blocks: BetaContentBlock[] = [];
  c.set({ draft: [] });
  return new Promise((resolve) => {
    let idle: ReturnType<typeof setTimeout> | undefined;
    let heard = false;
    const show = () => c.set({ draft: blocks.map((b) => ({ ...b })) });
    const finish = (ok: boolean, error?: { message: string; code: string | null }) => {
      const l = listeners.get(turn);
      if (!l) return;
      if (idle) clearTimeout(idle);
      if (l.grace) clearTimeout(l.grace);
      if (l.quiet) clearTimeout(l.quiet);
      listeners.delete(turn);
      if (c.get()?.turn === turn) c.set({ turn: null });
      dropPending(turn);
      // a question the turn left unanswered goes with it
      if (asks.some((a) => a.turn === turn)) {
        asks = asks.filter((a) => a.turn !== turn);
        showAsk(c.id);
      }
      if (c.get()?.question?.turn === turn) c.set({ question: null });
      // a call the agent never answered was stopped with the turn
      c.set((s) => ({ tools: Object.fromEntries(Object.entries(s.tools).map(([id, t]) => [id, t.status === "running" ? { status: "skipped" } : t])) }));
      // a reply another device kept in this chat first (it followed the turn too, and this page picked it up after) is not added again
      if (blocks.length && !(c.get()?.messages ?? []).some((m) => (m as RunnerReply).runner?.turn === turn)) {
        const reply: RunnerReply = { role: "assistant", content: blocks as unknown as BetaMessageParam["content"], runner: { agent, thread, host: get().runner?.host ?? null, turn }, ...(agent === "codex" ? { oai: true as const } : {}) };
        c.set((s) => ({ messages: [...s.messages, reply] }));
      }
      c.set({ draft: null });
      // a chat reopened from the list whose session the computer does not have says why, and what to do
      if (error && from && NO_SESSION.test(error.message)) c.set({ error: { ...noSessionError(agent === "codex" ? "Codex" : "Claude Code", sessionHost(c.get()?.messages ?? []), get().runner?.host ?? null), action: "new" } });
      else if (error && error.code !== "stopped") c.set({ error: { title: error.message, action: error.code === "offline" ? null : "retry" } });
      if (error?.code === "stopped") c.set({ stopped: "" });
      resolve(ok);
    };
    // a computer that stops answering for ten minutes ends the turn, so the chat never waits forever. The board is
    // asked first: a page that was away (a phone that locked) may only have missed the rest, or the end
    const arm = () => {
      if (idle) clearTimeout(idle);
      // a turn waiting on the user's answer waits as long as they take
      idle = setTimeout(() => {
        if (c.get()?.question?.turn === turn) return arm();
        void catchUp().then((running) => {
          if (!listeners.has(turn)) return;
          if (running) arm();
          else finish(false, running === null ? { message: "This page lost its link to the board. Reload it to see the reply.", code: "offline" } : { message: "Your computer stopped answering. Check the runner there, then try again.", code: "timeout" });
        });
      }, 10 * 60_000);
    };
    /**
     * A call whose yes was asked for before its own line came (Codex asks before it starts the call, and Claude Code
     * can too) takes over the line its ask made, with the user's answer: one call is one line, never a "Using" line
     * left beside a "Used" one (Round 7).
     */
    const claim = (ev: { id: string; name: string; input?: unknown; status: string }) => {
      if (blocks.some((b) => b.type === "tool_use" && b.id === ev.id)) return;
      const open = blocks.filter((b): b is Extract<BetaContentBlock, { type: "tool_use" }> => b.type === "tool_use" && !!(b as { asked?: boolean }).asked && b.name === ev.name);
      const said = (b: { id: string }) => c.get()?.tools[b.id]?.status;
      // the ask still waiting, or allowed and not yet begun; one the user said no to only by that call's own failure
      const ask = open.find((b) => said(b) === "waiting" || said(b) === "running") ?? (ev.status === "error" ? open.find((b) => said(b) === "declined") : undefined);
      if (!ask) return;
      const was = ask.id;
      ask.id = ev.id;
      if (ev.input !== undefined) ask.input = ev.input as Record<string, unknown>;
      delete (ask as { asked?: boolean }).asked;
      c.set((s) => {
        const { [was]: kept, ...rest } = s.tools;
        return { tools: { ...rest, ...(kept ? { [ev.id]: kept } : {}) } };
      });
      // a yes still to give is given on that line now
      if (asks.some((a) => a.toolId === was)) {
        asks = asks.map((a) => (a.toolId === was ? { ...a, toolId: ev.id } : a));
        showAsk(c.id);
      }
    };
    // the number of the last event built into the reply, and those that came while the board was being asked
    let seq = 0;
    let held: { ev: RunnerEvent; seq: number }[] = [];
    let catching: Promise<boolean | null> | null = null;
    // the board has the turn: one sent from here once it said so, one followed from elsewhere at once
    let sent = !send;
    const catchUp = (): Promise<boolean | null> => {
      if (catching) return catching;
      catching = api<{ events: { seq: number; event: RunnerEvent }[]; running: boolean }>(`/api/chat/events?turn=${encodeURIComponent(turn)}`)
        .then((r) => {
          const top = r.events.at(-1)?.seq ?? 0;
          if (listeners.has(turn) && top > seq) {
            // built again from the start, since words that ran on are kept as one: what this turn showed goes first
            blocks.length = 0;
            if (asks.some((a) => a.turn === turn)) {
              asks = asks.filter((a) => a.turn !== turn);
              showAsk(c.id);
            }
            if (c.get()?.question?.turn === turn) c.set({ question: null });
            seq = top;
            for (const e of r.events) if (listeners.has(turn)) apply(e.event);
            if (listeners.has(turn)) show();
          }
          // over on the board, with nothing kept that ended it here: an hour gone, or from before the board kept them
          if (!r.running && sent && listeners.has(turn)) finish(false, { message: blocks.length ? "This page missed the end of that reply, and the board no longer has it." : "This page missed that reply, and the board no longer has it.", code: "offline" });
          return r.running;
        })
        .catch(() => null)
        .finally(() => {
          catching = null;
          const later = held;
          held = [];
          for (const x of later) receive(x.ev, x.seq);
        });
      return catching;
    };
    // an event as it comes: one the reply has is left, and one after a gap asks the board for what was missed
    const receive = (ev: RunnerEvent, n?: number) => {
      if (n === undefined) return apply(ev);
      if (catching) return void held.push({ ev, seq: n });
      if (n <= seq) return;
      if (n > seq + 1) {
        void catchUp();
        held.push({ ev, seq: n });
        return;
      }
      seq = n;
      apply(ev);
    };
    const apply = (ev: RunnerEvent) => {
      heard = true;
      arm();
      switch (ev.kind) {
        case "started":
          thread = ev.thread ?? thread;
          break;
        case "text": {
          const last = blocks.at(-1);
          if (last?.type === "text" && !(last as { notice?: boolean }).notice) last.text += ev.text;
          else blocks.push({ type: "text", text: ev.text, citations: null } as BetaContentBlock);
          show();
          break;
        }
        case "thinking": {
          const last = blocks.at(-1);
          if (last?.type === "thinking") last.thinking += ev.text;
          else blocks.push({ type: "thinking", thinking: ev.text, signature: "" } as BetaContentBlock);
          show();
          break;
        }
        case "tool":
          claim(ev);
          if (ev.status === "running") {
            if (!blocks.some((b) => b.type === "tool_use" && b.id === ev.id)) blocks.push({ type: "tool_use", id: ev.id, name: ev.name, input: (ev.input ?? {}) as Record<string, unknown> } as BetaContentBlock);
            // one still waiting on the user's yes (its ask came first) keeps waiting
            c.set((s) => ({ tools: { ...s.tools, [ev.id]: s.tools[ev.id]?.status === "waiting" ? s.tools[ev.id] : { ...keepAsked(s.tools[ev.id]), status: "running" } } }));
            show();
          } else {
            // a step that only says it finished, like a picture Codex made, still shows as a line
            if (!blocks.some((b) => b.type === "tool_use" && b.id === ev.id)) {
              blocks.push({ type: "tool_use", id: ev.id, name: ev.name, input: (ev.input ?? {}) as Record<string, unknown> } as BetaContentBlock);
              show();
            }
            // a call the user said no to stays theirs, whatever the agent then says of it
            // and a question's line keeps the user's own answer, not the agent's note of it
            c.set((s) => {
              if (s.tools[ev.id]?.status === "declined" && ev.status === "error") return {};
              const answered = s.tools[ev.id]?.text?.startsWith("You answered: ") ? s.tools[ev.id]!.text : undefined;
              const text = answered ?? ev.result;
              return { tools: { ...s.tools, [ev.id]: { ...keepAsked(s.tools[ev.id]), status: ev.status === "ok" ? "ok" : "error", ...(text ? { text } : {}) } } };
            });
          }
          break;
        case "approval": {
          // a call on one of the user's own servers waits for their yes, shown on its line: the one it named,
          // else the last of that tool still running, else a line of its own
          const named = ev.toolId && blocks.some((b) => b.type === "tool_use" && b.id === ev.toolId) ? ev.toolId : null;
          const running = [...blocks].reverse().find((b): b is Extract<BetaContentBlock, { type: "tool_use" }> => b.type === "tool_use" && b.name === ev.tool && c.get()?.tools[b.id]?.status === "running");
          const toolId = named ?? running?.id ?? ev.id;
          if (toolId === ev.id) {
            // marked, so the call it asks about takes this line over when its own comes (claim)
            blocks.push({ type: "tool_use", id: ev.id, name: ev.tool, input: {}, asked: true } as BetaContentBlock);
            show();
          }
          // and keeps that it asked, and why in a hands-free run, once it is answered (Features 4)
          c.set((s) => ({ tools: { ...s.tools, [toolId]: { status: "waiting", asked: ev.why ?? "yes" } } }));
          asks = [...asks.filter((a) => !(a.turn === turn && a.id === ev.id)), { turn, chat: c.id, id: ev.id, toolId, title: ev.title, detail: ev.detail, ...(ev.why ? { why: ev.why } : {}) }];
          showAsk(c.id);
          break;
        }
        case "question": {
          // the agent's own question: on its line (the one it named, else a line of its own), waiting for the user
          const named = ev.toolId && blocks.some((b) => b.type === "tool_use" && b.id === ev.toolId) ? ev.toolId : null;
          const toolId = named ?? ev.id;
          if (!named) {
            blocks.push({ type: "tool_use", id: toolId, name: "ask_user", input: { questions: ev.questions.map((q) => q.question) } } as BetaContentBlock);
            show();
          }
          c.set((s) => ({ question: { turn, id: ev.id, toolId, questions: ev.questions }, tools: { ...s.tools, [toolId]: { status: "waiting" } } }));
          break;
        }
        case "answered": {
          // answered in another tab, or by the runner when nobody answered in time: the question comes down here too
          const q = c.get()?.question;
          if (q?.turn === turn && q.id === ev.id) c.set((s) => ({ question: null, tools: { ...s.tools, [q.toolId]: { status: "running", text: s.tools[q.toolId]?.text } } }));
          const a = asks.find((x) => x.turn === turn && x.id === ev.id);
          if (a) {
            asks = asks.filter((x) => x !== a);
            c.set((s) => ({ tools: { ...s.tools, [a.toolId]: { ...keepAsked(s.tools[a.toolId]), status: ev.allow ? "running" : "declined" } } }));
            showAsk(c.id);
          }
          break;
        }
        case "notice":
          // said as a line of its own in the reply, which the agent's next words never run into
          blocks.push({ type: "text", text: ev.text, citations: null, notice: true } as BetaContentBlock);
          show();
          break;
        case "done":
          thread = ev.thread ?? thread;
          if (ev.usage) c.set((s) => ({ usage: { ...s.usage, input: s.usage.input + ev.usage!.input, output: s.usage.output + ev.usage!.output } }));
          finish(true);
          break;
        case "error":
          finish(false, { message: ev.message, code: ev.code });
          break;
      }
    };
    listeners.set(turn, { turn, agent, on: receive, catchUp });
    arm();
    if (firstWord) {
      const quiet = setTimeout(() => !heard && finish(false, { message: "Your computer did not pick that up. Send it again.", code: "offline" }), firstWord);
      listeners.get(turn)!.quiet = quiet;
    }
    send?.()
      .then(() => (sent = true))
      .catch((e: Error) => finish(false, { message: e.message, code: "send" }));
  });
}
